<script setup lang="ts">
import { computed, ref, useSlots, watch } from 'vue';

import type {
	AppShellSidebarNavTarget,
	SidebarItem,
} from '@apexx/component-library';
import {
	Avatar,
	Icon,
	avatarBackgroundStyle,
	initialsFromName,
} from '@apexx/component-library';
import { useShellContext } from '@apexx/shell-contract';

import {
	collectExpandedLabelsForActiveRoute,
	isNavNodeActive,
	isNavTargetActive,
} from './dualPaneNavActive';
import { buildHubRailApps, type DualPaneRailApp } from './hubRailApps';

type LibIconName = NonNullable<InstanceType<typeof Icon>['$props']['name']>;

type JwtProfilePayload = {
	sub?: string;
	name?: string;
	preferred_username?: string;
	email?: string;
	picture?: string;
};

export interface DualPaneSidebarSubcategory {
	label: string;
	to: AppShellSidebarNavTarget;
	subcategories?: DualPaneSidebarSubcategory[];
}

export interface DualPaneSidebarItem extends SidebarItem {
	subcategories?: DualPaneSidebarSubcategory[];
}

const props = withDefaults(
	defineProps<{
		items?: DualPaneSidebarItem[];
		activeRoute?: string;
		/** Current hub app id — marks the matching rail icon active. */
		activeAppId: string;
		title?: string;
		/** Overrides the secondary panel heading (defaults to the active rail app label). */
		panelHeading?: string;
		/** When true (mobile drawer), sidebar spans full width. */
		showClose?: boolean;
		showAppSwitcher?: boolean;
		/** Pre-filtered rail apps (prefer from `useAppSwitcher`). */
		railApps?: DualPaneRailApp[];
		/** Used when `railApps` is omitted — filters via launcher access rules. */
		accessToken?: string;
	}>(),
	{
		items: () => [],
		title: '',
		showClose: false,
		showAppSwitcher: false,
	},
);

const emit = defineEmits<{
	navigate: [to: AppShellSidebarNavTarget];
	close: [];
	openSettings: [];
	openApp: [appId: string];
	openHubHome: [];
}>();

const slots = useSlots();
const shellContext = useShellContext();
const expandedParentLabels = ref<Set<string>>(new Set());

const resolvedAccessToken = computed(
	() => props.accessToken ?? shellContext?.accessToken,
);

const profileAvatar = computed(() => {
	const payload = decodeJwtProfile(resolvedAccessToken.value);
	const displayName =
		payload?.name?.trim() ||
		payload?.preferred_username?.trim() ||
		payload?.email?.trim() ||
		'';
	const initials = initialsFromName(displayName || '?');
	const seed = payload?.sub?.trim() || displayName || initials;
	const picture =
		typeof payload?.picture === 'string' && payload.picture.trim().length > 0
			? payload.picture.trim()
			: undefined;

	return {
		picture,
		initials,
		colorClass: '!text-white',
		colorStyle: avatarBackgroundStyle(seed),
	};
});

const resolvedRailApps = computed(() => {
	if (props.railApps !== undefined) {
		return props.railApps.map((app) => ({
			...app,
			active: app.id === props.activeAppId,
		}));
	}
	return buildHubRailApps(props.activeAppId, resolvedAccessToken.value);
});

const panelTitle = computed(() => {
	if (props.panelHeading) {
		return props.panelHeading;
	}
	const activeRail = resolvedRailApps.value.find((app) => app.active);
	return activeRail?.label ?? props.title;
});

const hasPanelSlot = computed(() => Boolean(slots.panel));
const hasPanelActionsSlot = computed(() => Boolean(slots['panel-actions']));

watch(
	() => [props.activeRoute, props.items] as const,
	([activeRoute, items]) => {
		const labels = collectExpandedLabelsForActiveRoute(
			items ?? [],
			activeRoute,
		);
		if (labels.length === 0) {
			return;
		}

		const next = new Set(expandedParentLabels.value);
		for (const label of labels) {
			next.add(label);
		}
		expandedParentLabels.value = next;
	},
	{ immediate: true },
);

function decodeJwtProfile(token: string | undefined): JwtProfilePayload | undefined {
	if (!token) {
		return undefined;
	}

	const parts = token.split('.');
	if (parts.length < 2) {
		return undefined;
	}

	try {
		const payload = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
		const parsed = JSON.parse(payload) as unknown;

		return typeof parsed === 'object' && parsed !== null
			? (parsed as JwtProfilePayload)
			: undefined;
	} catch {
		return undefined;
	}
}

function isParentExpanded(label: string): boolean {
	return expandedParentLabels.value.has(label);
}

function toggleParentGroup(label: string): void {
	const next = new Set(expandedParentLabels.value);
	if (next.has(label)) {
		next.delete(label);
	} else {
		next.add(label);
	}
	expandedParentLabels.value = next;
}

function navigate(to: AppShellSidebarNavTarget): void {
	emit('navigate', to);
}

function onRailAppClick(app: DualPaneRailApp): void {
	if (app.active) {
		return;
	}
	if (props.showAppSwitcher) {
		emit('openApp', app.id);
	}
}

function asIcon(icon: string): LibIconName {
	return icon as LibIconName;
}

function panelItemClass(isActive: boolean, extras: string[] = []): string {
	return [
		'relative mb-1 flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors duration-normal md:gap-3.5 md:px-4 md:py-3.5',
		isActive
			? 'bg-primary/10 text-primary border-l-6 border-primary'
			: 'text-gray-500 hover:bg-gray-50 hover:text-gray-700 border-l-6 border-transparent',
		...extras,
	].join(' ');
}
</script>

<template>
	<aside
		:class="[
			'flex h-full min-h-0 min-w-0 overflow-hidden border-r border-gray-200 bg-white shadow-sm',
			showClose ? 'w-full' : 'w-max',
		]"
		:style="{
			paddingTop: 'var(--safe-area-top)',
			paddingBottom: 'var(--safe-area-bottom)',
		}"
	>
		<!-- Primary rail (hidden when app switcher is unavailable, e.g. locked-in clients) -->
		<div
			v-if="showAppSwitcher"
			class="flex w-19 shrink-0 flex-col items-center border-r border-gray-200 py-3 md:w-28 md:py-4"
		>
			<nav
				class="flex flex-1 flex-col items-center gap-4 overflow-y-auto px-1 md:gap-5"
			>
				<button
					type="button"
					class="flex w-full flex-col items-center gap-1 md:gap-1.5"
					aria-label="Profile"
					@click="emit('openHubHome')"
				>
					<Avatar
						:src="profileAvatar.picture"
						:initials="profileAvatar.initials"
						size="md"
						class="md:!h-14 md:!w-14 md:!text-title"
						:class="[profileAvatar.colorClass, 'hover:opacity-90']"
						:style="profileAvatar.colorStyle"
					/>
					<span
						class="max-w-full px-0.5 text-center text-xs leading-tight whitespace-normal break-words line-clamp-2 text-gray-700 md:text-xs"
					>
						Profile
					</span>
				</button>

				<button
					v-for="app in resolvedRailApps"
					:key="app.id"
					type="button"
					class="flex w-full flex-col items-center gap-1 md:gap-1.5"
					:aria-current="app.active ? 'page' : undefined"
					@click="onRailAppClick(app)"
				>
					<span
						:class="[
							'flex h-10 w-10 items-center justify-center rounded-xl transition-colors duration-normal md:h-12! md:w-12!',
							app.active
								? 'bg-primary text-white'
								: 'border border-gray-300 text-gray-700',
						]"
					>
						<Icon
							:name="asIcon(app.icon)"
							size="md"
							class="md:h-6! md:w-6!"
						/>
					</span>
					<span
						:class="[
							'max-w-full px-0.5 text-center text-[12px] leading-tight whitespace-normal break-words line-clamp-2 md:text-xs',
							app.active ? 'font-medium text-primary' : 'text-gray-700',
						]"
					>
						{{ app.label }}
					</span>
				</button>
			</nav>

			<button
				type="button"
				class="mt-3 flex h-10 w-10 items-center justify-center rounded-xl border border-gray-300 text-gray-700 transition-colors hover:border-gray-300 hover:text-gray-600 md:mt-4 md:h-12! md:w-12!"
				aria-label="Settings"
				@click="emit('openSettings')"
			>
				<Icon name="settings" size="md" class="md:h-6! md:w-6!" />
			</button>
		</div>

		<!-- Secondary panel -->
		<div
			:class="[
				'flex h-full min-h-0 min-w-0 flex-col overflow-hidden',
				showClose ? 'flex-1' : 'w-68 shrink-0 md:w-80',
			]"
		>
			<div
				v-if="panelTitle || showClose || hasPanelActionsSlot"
				class="flex shrink-0 items-center gap-2 px-4 pb-2 pt-4 md:px-5 md:pb-3 md:pt-5"
			>
				<h2
					class="min-w-0 flex-1 truncate text-base font-semibold text-gray-700 md:text-lg"
				>
					{{ panelTitle }}
				</h2>
				<div v-if="hasPanelActionsSlot" class="shrink-0">
					<slot name="panel-actions" />
				</div>
				<button
					v-if="showClose"
					type="button"
					class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-primary md:h-11 md:w-11"
					aria-label="Close menu"
					@click="emit('close')"
				>
					<Icon name="cancel" size="md" class="md:h-6! md:w-6!" />
				</button>
			</div>

			<div
				v-if="hasPanelSlot"
				class="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
			>
				<slot name="panel" />
			</div>

			<nav v-else class="min-h-0 min-w-0 flex-1 overflow-y-auto px-2 py-2 md:px-3 md:py-3">
				<template v-for="item in items" :key="item.to + item.label">
					<template v-if="item.subcategories?.length">
						<button
							type="button"
							:class="panelItemClass(isNavNodeActive(item, activeRoute))"
							@click="toggleParentGroup(item.label)"
						>
							<Icon
								:name="asIcon(item.icon)"
								size="sm"
								:class="[
									isNavNodeActive(item, activeRoute)
										? 'text-primary'
										: 'text-gray-400',
									'md:h-5! md:w-5!',
								]"
							/>
							<span class="min-w-0 flex-1 truncate text-sm md:text-base">{{ item.label }}</span>
							<span
								v-if="item.badge"
								class="rounded-full bg-primary/15 px-1.5 py-0.5 text-[12px] font-bold text-primary md:px-2 md:text-xs"
							>
								{{ item.badge }}
							</span>
							<Icon
								name="chevronRight"
								size="sm"
								:class="[
									isNavNodeActive(item, activeRoute)
										? 'text-primary'
										: 'text-gray-400',
									isParentExpanded(item.label) ? 'rotate-90' : '',
									'md:h-5! md:w-5!',
								]"
							/>
						</button>

						<template v-if="isParentExpanded(item.label)">
							<button
								v-for="sub in item.subcategories"
								:key="sub.label + sub.to"
								type="button"
								:class="
									panelItemClass(isNavTargetActive(activeRoute, sub.to), [
										'pl-10 md:pl-12',
									])
								"
								@click="navigate(sub.to)"
							>
								<span class="min-w-0 flex-1 truncate text-sm md:text-base">{{ sub.label }}</span>
							</button>
						</template>
					</template>

					<button
						v-else
						type="button"
						:class="panelItemClass(isNavTargetActive(activeRoute, item.to))"
						@click="navigate(item.to)"
					>
						<Icon
							:name="asIcon(item.icon)"
							size="sm"
							:class="[
								isNavTargetActive(activeRoute, item.to)
									? 'text-primary'
									: 'text-gray-400',
								'md:h-5! md:w-5!',
							]"
						/>
						<span class="min-w-0 flex-1 truncate text-sm md:text-base">{{ item.label }}</span>
						<span
							v-if="item.badge"
							class="rounded-full bg-primary/15 px-1.5 py-0.5 text-[12px] font-bold text-primary md:px-2 md:text-xs"
						>
							{{ item.badge }}
						</span>
					</button>
				</template>
			</nav>

			<!-- Clients (no app rail): settings lives in the nav pane and must stay reachable -->
			<button
				v-if="!showAppSwitcher"
				type="button"
				:class="panelItemClass(false, ['mx-2', 'mb-2', 'mt-auto', 'shrink-0'])"
				aria-label="Settings"
				@click="emit('openSettings')"
			>
				<Icon name="settings" size="sm" class="text-gray-400" />
				<span class="min-w-0 flex-1 truncate text-sm">Settings</span>
			</button>
		</div>
	</aside>
</template>
